import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  sendEmailVerification
} from 'firebase/auth'
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../firebase'

const AuthContext = createContext()

const FUNCTIONS_BASE_URL = 'https://us-central1-voyabay-hotels.cloudfunctions.net'

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const recaptchaVerifierRef = useRef(null)
  const confirmationResultRef = useRef(null)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('[Auth] Auth state changed:', firebaseUser?.email || firebaseUser?.phoneNumber || 'null')
      setUser(firebaseUser)
      
      if (firebaseUser) {
        // Fetch user data from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid))
          let firestoreData = userDoc.exists() ? userDoc.data() : {}
          
          // Check if email is verified and process pending referral
          if (firebaseUser.email && firebaseUser.emailVerified && firestoreData.pendingReferralCode) {
            // User's email is verified but we haven't processed the referral yet
            if (!firestoreData.emailVerified) {
              console.log('[Auth] Processing verified referral on auth state change...')
              const userDocRef = doc(db, 'users', firebaseUser.uid)
              
              // Update Firestore to mark as verified
              await updateDoc(userDocRef, {
                emailVerified: true,
                emailVerifiedAt: serverTimestamp()
              })
              
              // Process pending referral
              try {
                const response = await fetch(`${FUNCTIONS_BASE_URL}/processVerifiedReferral`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    firebaseUid: firebaseUser.uid,
                    referralCode: firestoreData.pendingReferralCode
                  })
                })
                const data = await response.json()
                console.log('[Auth] Processed referral on auth change:', data)
                
                // Clear pending referral code
                await updateDoc(userDocRef, { pendingReferralCode: null })
                
                // Update local data
                firestoreData = { ...firestoreData, emailVerified: true, pendingReferralCode: null }
              } catch (err) {
                console.error('[Auth] Error processing referral on auth change:', err)
              }
            }
          }
          
          // Also fetch userId from Cloud Functions API (this is the booking system userId)
          // Use firebaseUid for phone users, email for email/google users
          try {
            const queryParam = firebaseUser.email 
              ? `email=${encodeURIComponent(firebaseUser.email)}`
              : `firebaseUid=${encodeURIComponent(firebaseUser.uid)}`
            
            const response = await fetch(
              `${FUNCTIONS_BASE_URL}/getUserProfile?${queryParam}`
            )
            if (response.ok) {
              const data = await response.json()
              if (data.user) {
                // Merge the backend user data with Firestore data
                firestoreData = {
                  ...firestoreData,
                  userId: data.user.userId,
                  staysCount: data.user.staysCount ?? firestoreData.staysCount ?? 0,
                  rewardVouchers: data.user.rewardVouchers || firestoreData.rewardVouchers || [],
                  backendUser: data.user  // Store full backend user for reference
                }
                console.log('[Auth] Backend userId:', data.user.userId)
              }
            }
          } catch (apiError) {
            console.error('[Auth] Error fetching backend user:', apiError)
          }
          
          setUserData(firestoreData)
        } catch (error) {
          console.error('Error fetching user data:', error)
        }
      } else {
        setUserData(null)
      }
      
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const login = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password)
    
    // Link to backend booking system user (creates one if doesn't exist)
    // This handles users who made bookings before creating an account
    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/linkFirebaseAuth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          firebaseUid: result.user.uid,
          provider: 'password'
        })
      })
      const data = await response.json()
      console.log('[Auth] Linked to backend user system:', data)
    } catch (linkError) {
      console.error('[Auth] Error linking to backend:', linkError)
    }
    
    return result
  }

  const signUp = async (email, password, name, referralCode = null) => {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    
    // Send verification email
    await sendEmailVerification(result.user)
    console.log('[Auth] Verification email sent to:', email)
    
    // Create user document in Firestore (Firebase Auth users collection)
    await setDoc(doc(db, 'users', result.user.uid), {
      email,
      name,
      createdAt: serverTimestamp(),
      staysCount: 0,
      rewardVouchers: [],
      currency: 'USD',
      language: 'en',
      referredBy: referralCode || null,  // Track referral
      emailVerified: false,  // Track verification status
      pendingReferralCode: referralCode || null  // Store for when verified
    })
    
    // Link to backend booking system user (creates one if doesn't exist)
    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/linkFirebaseAuth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          firebaseUid: result.user.uid,
          displayName: name,
          provider: 'password',
          referralCode: referralCode || null  // Pass to backend
        })
      })
      const data = await response.json()
      console.log('[Auth] Linked to backend user system:', data)
    } catch (linkError) {
      console.error('[Auth] Error linking to backend:', linkError)
    }
    
    return result
  }

  const loginWithGoogle = async (referralCode = null) => {
    const provider = new GoogleAuthProvider()
    console.log('[Auth] Initiating Google popup...')
    try {
      const result = await signInWithPopup(auth, provider)
      console.log('[Auth] Google login success:', result.user.email)
      
      // Check if user exists, if not create document
      const userDocRef = doc(db, 'users', result.user.uid)
      const userDoc = await getDoc(userDocRef)
      const isNewUser = !userDoc.exists()
      
      if (isNewUser) {
        console.log('[Auth] Creating new user document...')
        await setDoc(userDocRef, {
          email: result.user.email,
          name: result.user.displayName || '',
          createdAt: serverTimestamp(),
          staysCount: 0,
          rewardVouchers: [],
          currency: 'USD',
          language: 'en',
          referredBy: referralCode || null,  // Track referral for new users
          emailVerified: true,  // Google accounts have verified emails
          emailVerifiedAt: serverTimestamp()
        })
        console.log('[Auth] User document created')
        
        // Google users are auto-verified, process referral immediately
        if (referralCode) {
          try {
            const response = await fetch(`${FUNCTIONS_BASE_URL}/processVerifiedReferral`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                firebaseUid: result.user.uid,
                referralCode: referralCode
              })
            })
            const data = await response.json()
            console.log('[Auth] Referral processed for Google user:', data)
          } catch (refError) {
            console.error('[Auth] Error processing referral:', refError)
          }
        }
      }
      
      // Link to backend booking system user (creates one if doesn't exist)
      try {
        const response = await fetch(`${FUNCTIONS_BASE_URL}/linkFirebaseAuth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: result.user.email.toLowerCase().trim(),
            firebaseUid: result.user.uid,
            displayName: result.user.displayName || '',
            photoURL: result.user.photoURL || null,
            provider: 'google.com',
            referralCode: isNewUser ? (referralCode || null) : null  // Only pass referral for new users
          })
        })
        const data = await response.json()
        console.log('[Auth] Linked to backend user system:', data)
      } catch (linkError) {
        console.error('[Auth] Error linking to backend:', linkError)
      }
      
      return result
    } catch (error) {
      console.error('[Auth] Google login error:', error)
      throw error
    }
  }

  const setupRecaptcha = (elementId) => {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear()
    }
    
    recaptchaVerifierRef.current = new RecaptchaVerifier(auth, elementId, {
      size: 'invisible',
      callback: () => {
        console.log('[Auth] reCAPTCHA solved')
      },
      'expired-callback': () => {
        console.log('[Auth] reCAPTCHA expired')
      }
    })
    
    return recaptchaVerifierRef.current
  }

  const sendPhoneVerification = async (phoneNumber, elementId) => {
    try {
      const recaptchaVerifier = setupRecaptcha(elementId)
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier)
      confirmationResultRef.current = confirmationResult
      console.log('[Auth] SMS sent successfully')
      return { success: true }
    } catch (error) {
      console.error('[Auth] Phone verification error:', error)
      // Clear reCAPTCHA on error
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear()
        recaptchaVerifierRef.current = null
      }
      throw error
    }
  }

  const confirmPhoneCode = async (code, referralCode = null) => {
    if (!confirmationResultRef.current) {
      throw new Error('No pending verification. Please request a new code.')
    }
    
    try {
      const result = await confirmationResultRef.current.confirm(code)
      console.log('[Auth] Phone login success:', result.user.phoneNumber)
      
      // Check if user exists, if not create document
      const userDocRef = doc(db, 'users', result.user.uid)
      const userDoc = await getDoc(userDocRef)
      const isNewUser = !userDoc.exists()
      
      if (isNewUser) {
        console.log('[Auth] Creating new user document for phone user...')
        await setDoc(userDocRef, {
          phone: result.user.phoneNumber,
          createdAt: serverTimestamp(),
          staysCount: 0,
          rewardVouchers: [],
          currency: 'USD',
          language: 'en',
          referredBy: referralCode || null  // Track referral
        })
        console.log('[Auth] User document created')
      }
      
      // Link to backend booking system user (creates one if doesn't exist)
      try {
        const response = await fetch(`${FUNCTIONS_BASE_URL}/linkFirebaseAuth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone: result.user.phoneNumber,
            firebaseUid: result.user.uid,
            provider: 'phone',
            referralCode: isNewUser ? (referralCode || null) : null  // Only pass referral for new users
          })
        })
        const data = await response.json()
        console.log('[Auth] Linked to backend user system:', data)
      } catch (linkError) {
        console.error('[Auth] Error linking to backend:', linkError)
      }
      
      return result
    } catch (error) {
      console.error('[Auth] Code confirmation error:', error)
      throw error
    }
  }

  const logout = () => {
    return signOut(auth)
  }

  // Resend verification email
  const resendVerificationEmail = async () => {
    if (user && !user.emailVerified) {
      await sendEmailVerification(user)
      console.log('[Auth] Verification email resent to:', user.email)
      return true
    }
    return false
  }
  
  // Check if email is verified and process pending referral
  const checkEmailVerification = async () => {
    if (!user) return false
    
    await user.reload()
    const currentUser = auth.currentUser
    
    if (currentUser?.emailVerified) {
      // Update Firestore to mark as verified
      const userDocRef = doc(db, 'users', currentUser.uid)
      const userDoc = await getDoc(userDocRef)
      
      if (userDoc.exists()) {
        const userData = userDoc.data()
        
        // If not already marked as verified, update and process referral
        if (!userData.emailVerified) {
          await updateDoc(userDocRef, {
            emailVerified: true,
            emailVerifiedAt: serverTimestamp()
          })
          console.log('[Auth] Email verified, updated Firestore')
          
          // Process pending referral if exists
          if (userData.pendingReferralCode) {
            try {
              const response = await fetch(`${FUNCTIONS_BASE_URL}/processVerifiedReferral`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  firebaseUid: currentUser.uid,
                  referralCode: userData.pendingReferralCode
                })
              })
              const data = await response.json()
              console.log('[Auth] Processed referral:', data)
              
              // Clear pending referral code
              await updateDoc(userDocRef, { pendingReferralCode: null })
            } catch (err) {
              console.error('[Auth] Error processing referral:', err)
            }
          }
        }
      }
      
      return true
    }
    
    return false
  }

  const refreshUserData = async () => {
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        let firestoreData = userDoc.exists() ? userDoc.data() : {}
        
        // Also refresh from Cloud Functions API
        // Use firebaseUid for phone users, email for email/google users
        try {
          const queryParam = user.email 
            ? `email=${encodeURIComponent(user.email)}`
            : `firebaseUid=${encodeURIComponent(user.uid)}`
          
          const response = await fetch(
            `${FUNCTIONS_BASE_URL}/getUserProfile?${queryParam}`
          )
          if (response.ok) {
            const data = await response.json()
            if (data.user) {
              firestoreData = {
                ...firestoreData,
                userId: data.user.userId,
                staysCount: data.user.staysCount ?? firestoreData.staysCount ?? 0,
                rewardVouchers: data.user.rewardVouchers || firestoreData.rewardVouchers || [],
                backendUser: data.user
              }
            }
          }
        } catch (apiError) {
          console.error('[Auth] Error refreshing backend user:', apiError)
        }
        
        setUserData(firestoreData)
      } catch (error) {
        console.error('Error refreshing user data:', error)
      }
    }
  }

  const value = {
    user,
    userData,
    loading,
    login,
    signUp,
    loginWithGoogle,
    sendPhoneVerification,
    confirmPhoneCode,
    logout,
    refreshUserData,
    resendVerificationEmail,
    checkEmailVerification
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
